import React, { Component } from 'react';
import { withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import AuthenticationService from "../../../Services/AuthenticationService";
import Button from "@material-ui/core/Button";
import FormLabel from "@material-ui/core/FormLabel";
import { isNotEmpty } from "../../../utils/Validator";
import GlobalMessage from "../../GlobalMessage";
import { Autocomplete } from "@material-ui/lab";
import DeleteIcon from '@material-ui/icons/Delete'; 
import IconButton from "@material-ui/core/IconButton";
import LoadingMsgBar from "../../LoadingMsgBar.js";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import './UploadRoster.css';

// Match styles approach of AddUser with minimal styling
const styles = () => ({
    root: {
        width: '100%',
    },
    errorText: {
        color: '#B0170C'
    },
});

window.analytics = { pageName: 'Upload Roster' };

class UploadRoster extends Component {
    constructor(props) {
        super(props);
        this.fileInput = React.createRef();
        this.state = {
            providerGroups: [],
            providerGroupsData: [],
            selectedProviderGroup: null,
            providerGroupOptions: [],
            loading: true,
            providerGroupError: '',
            selectedFiles: [], // Array of objects: { file: File, rosterType: 'full' | 'partial', hasError: false }
            fileError: '',
            globalMessage: '',
            successMessage: '',
            isSubmitEnabled: true
        };
    }
    
    componentDidMount() {
        // Clear any existing messages when component mounts
        this.clearGlobalMessages();
        
        // Format source groups from props for Provider Group selection
        if (this.props.sourceGroups && this.props.sourceGroups.length > 0) {
            // Convert source groups to options format for the dropdown
            let providerGroupOptions = [];
            
            // Add "Select One" as the first option, similar to AddUser page
            providerGroupOptions.push({
                value: '',
                label: 'Select One'
            });
            
            // Add provider groups from props
            this.props.sourceGroups.forEach(group => {
                providerGroupOptions.push({
                    value: group.sourceGroupId,
                    // Format the label to include both name and ID in parentheses
                    label: group.grpName 
                        ? `${group.grpName} (${group.grpId})` 
                        : `Group ID: ${group.grpId}`, // Fallback if grpName is missing,
                    groupId: group.grpId , // Include grpId for potential future use  
                    sourceId: group.sourceId // Include sourceId for potential future use 

                });
            });
            
            this.setState({ 
                isSubmitEnabled: this.props.isMirror ? false : true,
                providerGroupOptions,
                selectedProviderGroup: providerGroupOptions[0], // Set "Select One" as initial selection
                loading: false
            });
            console.log('Loaded source groups:', this.props.sourceGroups);
        } else {
            console.log('No source groups provided, using fallback data');
            // Add at least the "Select One" option
            const providerGroupOptions = [{
                value: '',
                label: 'Select One'
            }];
            
            this.setState({ 
                providerGroupOptions,
                selectedProviderGroup: providerGroupOptions[0], // Set "Select One" as initial selection
                loading: false
            });
        }
    }

    // Handle Provider Group selection
    handleProviderGroupSelected = (selectedProviderGroup) => {
        this.setState({ 
            selectedProviderGroup,
            providerGroupError: '' // Clear any error when a selection is made
        });
        
        if (selectedProviderGroup) {
            console.log('Selected provider group:', selectedProviderGroup);
            
            // If "Select One" is chosen (empty value), treat it as no selection for validation purposes
            if (selectedProviderGroup.value === '') {
                console.log('Select One option chosen - will require actual selection for validation');
            }
            // You could fetch additional data here based on the selected provider group if needed
        }
    }
    
    // Handle roster type change for individual file
    handleFileRosterTypeChange = (index, newType) => {
        this.setState(prevState => {
            const updatedFiles = [...prevState.selectedFiles];
            if (index >= 0 && index < updatedFiles.length) {
                updatedFiles[index] = {
                    ...updatedFiles[index],
                    rosterType: newType,
                    hasError: false // Clear error when user selects a value
                };
            }
            
            // Check if all files now have valid roster types selected
            const hasAnyErrors = updatedFiles.some(fileObj => !fileObj.rosterType || fileObj.rosterType === '');
            
            // Clear the file error message if all files have valid roster types
            return { 
                selectedFiles: updatedFiles,
                fileError: hasAnyErrors ? prevState.fileError : ''
            };
        });
    }

    // Handle file selection from the file input
    handleFileSelect = (event) => {
        console.log("File selection event triggered");
    
        if (!event) {
            console.error("File selection event is undefined");
            return;
        }
        
        if (!event.target) {
            console.error("File selection event target is undefined");
            return;
        }
        
        if (!event.target.files) {
            console.error("No files in selection event");
            return;
        }
        if (event.target.files && event.target.files.length > 0) {
            const files = Array.from(event.target.files);
            
            // Check for duplicate files
            const existingFileNames = this.state.selectedFiles.map(fileObj => fileObj.file.name);
            const newFiles = [];
            const duplicateFiles = [];
            
            files.forEach(file => {
                if (existingFileNames.includes(file.name)) {
                    duplicateFiles.push(file.name);
                } else {
                    newFiles.push(file);
                }
            });
            
            // Show error message if there are duplicates
            if (duplicateFiles.length > 0) {
                const duplicateList = duplicateFiles.join(', ');
                this.setState({
                    fileError: `Error: The following file(s) have already been added: ${duplicateList}`
                });
            }
            
            // Add only non-duplicate files
            if (newFiles.length > 0) {
                // Create file objects with default roster type ''
                const newFileObjects = newFiles.map(file => ({
                    file: file,
                    rosterType: '', // Default to empty string (Select One)
                    hasError: false // Initialize error state
                }));
                
                this.setState({ 
                    selectedFiles: [...this.state.selectedFiles, ...newFileObjects],
                    fileError: duplicateFiles.length > 0 ? this.state.fileError : ''
                });
            }
            
            // Reset the file input to allow selecting the same file again later if needed
            if (this.fileInput && this.fileInput.current) {
                this.fileInput.current.value = '';
            }
        }
    }

    // Trigger file browser when browse button is clicked
    handleBrowseClick = () => {
        if (this.fileInput && this.fileInput.current) {
            this.fileInput.current.click();         
        }
    }

    // Validate the form
    validate = () => {
        let isValid = true;
        let errors = {};

        // Check provider group - either not selected or "Select One" is selected
        if (!this.state.selectedProviderGroup || 
            (this.state.selectedProviderGroup && this.state.selectedProviderGroup.value === '')) {
            errors.providerGroupError = "Error: Select Provider Group";
            isValid = false;
        }

        // Check if at least one file is selected
        if (this.state.selectedFiles.length === 0) {
            errors.fileError = "Error: Please select at least one file to upload";
            isValid = false;
        }

        // Check if all files have a roster type selected and mark errors
        const updatedFiles = this.state.selectedFiles.map(fileObj => ({
            ...fileObj,
            hasError: !fileObj.rosterType || fileObj.rosterType === ''
        }));
        
        const hasUnselectedRosterType = updatedFiles.some(fileObj => fileObj.hasError);
        
        if (hasUnselectedRosterType) {
            errors.fileError = "Error: Please select a Roster Update Type for all files";
            isValid = false;
            // Update state with error flags on files
            this.setState({ selectedFiles: updatedFiles });
        }

        // Update state with any errors
        this.setState(errors);

        if (!isValid) {
            this.setGlobalErrorMessage('An error has occurred. Please address all fields marked with "Error".');
        }

        return isValid;
    }

    // Handle submit button click
    handleSubmitClick = () => {
        if (this.validate()) {
            // Disable submit button during processing
            this.setState({ 
                isSubmitEnabled: false,
                globalMessage: '',
                successMessage: '' 
            });
            
            // Extract necessary data for the API call
            const groupId = this.state.selectedProviderGroup ? this.state.selectedProviderGroup.groupId : '';
            const groupName = this.state.selectedProviderGroup ? this.state.selectedProviderGroup.label : '';         
            const sourceId = this.state.selectedProviderGroup ? this.state.selectedProviderGroup.sourceId : null;
            
            // Group files by roster type for the new API structure
            // The API expects: Map<String, List<MultipartFile>> where key is roster type
            const filesGroupedByType = {};
            
            this.state.selectedFiles.forEach(fileObj => {
                // Map 'partial' to 'delta' for API compatibility
                const rosterType = fileObj.rosterType === 'partial' ? 'delta' : 'full';
                
                // Initialize array if it doesn't exist
                if (!filesGroupedByType[rosterType]) {
                    filesGroupedByType[rosterType] = [];
                }
                
                // Add file to the appropriate roster type group
                filesGroupedByType[rosterType].push(fileObj.file);
            });
            
            console.log('Files grouped by roster type:', filesGroupedByType);
            
            // Call the updated upload method with grouped files
            this.uploadFiles(sourceId, groupName, groupId, filesGroupedByType);
        }
    }
    
    // Upload files grouped by roster type - Updated for new API
    uploadFiles = (sourceId, groupName, groupId, filesGroupedByType) => {
        AuthenticationService.uploadRosterFiles(sourceId, groupName, groupId, filesGroupedByType)
            .then(response => {
                console.log('Roster upload successful:', response);
                this.setGlobalSuccessMessage('Roster file(s) uploaded successfully.');
                this.setState({ isSubmitEnabled: true });
                
                setTimeout(() => {
                    if (this.props.onUploadComplete) {
                        this.props.onUploadComplete();
                    }
                }, 1000);
            })
            .catch(error => {
                console.error('Error uploading roster files:', error);
                let errorMessage = 'Error: There was a problem uploading the roster files. Please try again later.';
                
                if (error.response && error.response.data) {
                    errorMessage = `Error uploading roster files: ${error.response.data}`;
                } else if (error.message) {
                    errorMessage = `Error uploading roster files: ${error.message}`;
                }
                
                this.setState({ errorMsg: errorMessage, isSubmitEnabled: true });
            });
    }

    // Handle reset button click
    handleResetClick = () => {
        // Reset the file input value by recreating it
        if (this.fileInput && this.fileInput.current) {
            this.fileInput.current.value = '';
        }
        
        // Get the "Select One" option
        const selectOneOption = this.state.providerGroupOptions.length > 0 ? 
            this.state.providerGroupOptions[0] : 
            { value: '', label: 'Select One' };
        
        this.setState({
            selectedProviderGroup: selectOneOption, 
            providerGroupError: '',
            selectedFiles: [],
            fileError: '',
            globalMessage: '',
            errorMsg: '',
            successMessage: '', 
            isSubmitEnabled: true
        });
    }

    // Handle cancel button click
    handleCancelClick = () => {
        // Go back or close the upload page
        if (this.props.onCancel) {
            this.props.onCancel();
        }
    }

    // Set global error message
    setGlobalErrorMessage(message) {
        this.setState({ globalMessage: message });
    }

    // Set global success message
    setGlobalSuccessMessage(message) {
        this.setState({ successMessage: message });
    }

    // Clear global messages
    clearGlobalMessages() {
        this.setState({ 
            globalMessage: '',
            successMessage: ''
        });
    }
    
    // Remove a file from the selected files list
    handleRemoveFile = (index) => {
        // Check if index is valid
        if (index < 0 || index >= this.state.selectedFiles.length) {
            return;
        }
        
        this.setState(prevState => {
            // Create new array without the file at specified index
            const updatedFiles = prevState.selectedFiles.filter((_, idx) => idx !== index);
            return { selectedFiles: updatedFiles };
        }, () => {
            // After state is updated, reset file error if files remain
            if (this.state.selectedFiles.length > 0) {
                this.setState({ fileError: '' });
            }
            
            // Optionally reset the file input to keep it in sync with React state
            if (this.fileInput && this.fileInput.current) {
                this.fileInput.current.value = '';
            }
        });
    }

    showSuccessErrorMessage = () =>{
        return(
            <>
                <LoadingMsgBar loadMsg={this.state.loadingMessage}/>
                <GlobalMessage  message={this.state.errorMsg} />
                <GlobalMessage type ='success' message={this.state.successMsg} />

            <div className='App-note-Field App-error-font App-error-color'>
                Note: Fields marked with an asterisk(*) are required.
            </div></>
        );
    }

    render() {
        return (
            <div>
                <h1 className='App-page-label'>Upload Roster</h1>
                {this.showSuccessErrorMessage()}
                
                <Grid container direction='column' item xs={12}>
                    <Grid container spacing={3} style={{paddingBottom:'1rem'}}>
                        <Grid item style={{width:'50%', textAlign:'left'}}>
                            <Autocomplete
                                className={this.props.classes.autocomplete}
                                value={this.state.selectedProviderGroup}
                                options={this.state.providerGroupOptions}
                                getOptionLabel={(option) => option.label}
                                onChange={(event, newValue) => {
                                    this.handleProviderGroupSelected(newValue);
                                }}
                                disableClearable={true}
                                
                                noOptionsText="No provider groups available"
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Provider Group " 
                                        id="providerGroup"
                                        variant="outlined"
                                        
                                        fullWidth
                                        required
                                        helperText={this.state.providerGroupError}
                                        error={isNotEmpty(this.state.providerGroupError)}
                                        InputProps={{
                                            ...params.InputProps,
                                            
                                        }}
                                        FormHelperTextProps={{
                                            className: this.props.classes.errorText
                                        }}
                                        onBlur={() => {
                                            if (!this.state.selectedProviderGroup) {
                                                this.setState({
                                                    providerGroupError: "Error: Select Provider Group"
                                                });
                                            }
                                        }}
                                    />
                                )}
                                renderOption={(option) => (
                                    <div style={{ 
                                        padding: '4px 15px',
                                        width: '100%',
                                        fontFamily: "'Open Sans', sans-serif",
                                        fontSize: '16px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        {option.label}
                                    </div>
                                )}
                            />
                        </Grid>
                    </Grid>
                    
                    <Grid container spacing={3} style={{paddingBottom:'1rem'}}>
                        <Grid item xs={12}>
                            {/* Using the Material-UI file upload implementation instead of Mitchell component */}
                            <div className="file-upload-container">
                                <FormLabel component="legend" required style={{marginBottom: '8px'}}>Files </FormLabel>
                                <div className="file-upload-section">
                                    <input 
                                        type="file"
                                        ref={this.fileInput}
                                        style={{ display: 'none' }}
                                        onChange={this.handleFileSelect}
                                        accept="*"
                                        multiple
                                    />
                                    <div className="browse-button-container">
                                        <Button 
                                            variant="contained"
                                            color="primary"
                                            onClick={this.handleBrowseClick}
                                            aria-label="Select files to upload"
                                            className="browse-button"
                                        >
                                            BROWSE...
                                        </Button>
                                        <span className="file-instruction">
                                            Select one or more files
                                        </span>
                                    </div>
                                    
                                    {this.state.selectedFiles.length > 0 ? (
                                        <Paper className="selected-files-table-container">
                                            <Table className="cvsTable selected-files-table" size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell style={{width: '65%', fontWeight: 'bold'}}>File</TableCell>
                                                        <TableCell style={{width: '25%', fontWeight: 'bold'}}>Roster Update Type</TableCell>
                                                        <TableCell style={{width: '10%', fontWeight: 'bold', textAlign: 'center'}}>Action</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {this.state.selectedFiles.map((fileObj, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <span className="file-name-cell" title={fileObj.file.name}>
                                                                    {fileObj.file.name}
                                                                </span>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Select
                                                                    value={fileObj.rosterType}
                                                                    onChange={(e) => this.handleFileRosterTypeChange(index, e.target.value)}
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    style={{fontSize: '13px'}}
                                                                    displayEmpty
                                                                    error={fileObj.hasError}
                                                                    renderValue={(selected) => {
                                                                        if (!selected || selected === '') {
                                                                            return <span style={{color: 'rgba(0, 0, 0, 0.54)'}}>Select One</span>;
                                                                        }
                                                                        if (selected === 'full') {
                                                                            return 'Full';
                                                                        }
                                                                        if (selected === 'partial') {
                                                                            return 'Partial (Add/Change/Term)';
                                                                        }
                                                                        return selected;
                                                                    }}
                                                                >
                                                                    <MenuItem value="">Select One</MenuItem>
                                                                    <MenuItem value="full">Full</MenuItem>
                                                                    <MenuItem value="partial">Partial (Add/Change/Term)</MenuItem>
                                                                </Select>
                                                            </TableCell>
                                                            <TableCell style={{textAlign: 'center'}}>
                                                                <IconButton 
                                                                    size="small"
                                                                    onClick={() => this.handleRemoveFile(index)}
                                                                    className="remove-file-button"
                                                                    aria-label="Remove file"
                                                                    title="Remove file"
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Paper>
                                    ) : (
                                        <div className="no-files-selected">No files selected</div>
                                    )}
                                    
                                    {isNotEmpty(this.state.fileError) && (
                                        <div className="file-error-message">{this.state.fileError}</div>
                                    )}
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
                
                <div className='button-row' style={{marginTop: '1.5rem'}}>
                    <Button 
                        variant="contained"
                        color="primary"
                        onClick={this.handleSubmitClick}
                        disabled={!this.state.isSubmitEnabled}
                    >
                        SUBMIT
                    </Button>
                    <Button 
                        variant="outlined"
                        onClick={this.handleResetClick}
                    >
                        RESET
                    </Button>
                    <Button 
                        variant="outlined"
                        onClick={this.handleCancelClick}
                    >
                        CANCEL
                    </Button>
                </div>
            </div>
        );
    }
}

export default withStyles(styles)(UploadRoster);
